import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TableSortLabel
} from '@mui/material';
import { getFunnelSteps, BookingFunnelStep } from '../../firebase/services/abandonedCartService';
import { format } from 'date-fns';
import { Visibility } from '@mui/icons-material';
import BookingSessionDetail from './BookingSessionDetail';
import { Timestamp } from 'firebase/firestore';

type Order = 'asc' | 'desc';

const BookingFunnelDashboard: React.FC = () => {
  const [steps, setSteps] = useState<BookingFunnelStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<BookingFunnelStep['status'] | ''>('');
  const [sessions, setSessions] = useState<BookingFunnelStep[]>([]);
  const [selectedSession, setSelectedSession] = useState<BookingFunnelStep | null>(null);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof BookingFunnelStep>('timestamp');

  useEffect(() => {
    loadFunnelSteps();
  }, [status]);

  const loadFunnelSteps = async () => {
    try {
      setLoading(true);
      const data = await getFunnelSteps({
        status: status || undefined
      });
      console.log('Raw funnel data:', data);
      setSteps(data);
      setSessions(data);
      setError(null);
    } catch (err) {
      setError('Failed to load booking funnel data');
      console.error('Error loading funnel steps:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get unique sessions for rate calculations
  const uniqueSessions = React.useMemo(() => {
    const sessionMap = new Map();
    steps.forEach(step => {
      // Only take the latest step for each session
      if (!sessionMap.has(step.sessionId) || 
          new Date(step.timestamp) > new Date(sessionMap.get(step.sessionId).timestamp)) {
        sessionMap.set(step.sessionId, step);
      }
    });
    const unique = Array.from(sessionMap.values());
    console.log('Unique sessions:', unique);
    return unique;
  }, [steps]);

  // Calculate rates based on unique sessions
  const completionRate = React.useMemo(() => {
    const completedSessions = uniqueSessions.filter(s => s.status === 'completed').length;
    console.log('Completed sessions:', completedSessions);
    console.log('Total unique sessions:', uniqueSessions.length);
    return uniqueSessions.length ? (completedSessions / uniqueSessions.length) * 100 : 0;
  }, [uniqueSessions]);

  const abandonmentRate = React.useMemo(() => {
    const abandonedSessions = uniqueSessions.filter(s => s.status === 'abandoned').length;
    console.log('Abandoned sessions:', abandonedSessions);
    return uniqueSessions.length ? (abandonedSessions / uniqueSessions.length) * 100 : 0;
  }, [uniqueSessions]);

  const handleRequestSort = (property: keyof BookingFunnelStep) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getStatusColor = (status: BookingFunnelStep['status']) => {
    switch (status) {
      case 'in_progress':
        return 'primary';
      case 'completed':
        return 'success';
      case 'abandoned':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStepName = (step: string) => {
    const stepNames: { [key: string]: string } = {
      'start': 'Started Booking',
      'vessel_selection': 'Vessel Selection',
      'date_selection': 'Date Selection',
      'customer_info': 'Customer Information',
      'payment': 'Payment',
      'confirmation': 'Confirmation'
    };
    return stepNames[step] || step;
  };

  const formatTimestamp = (timestamp: any) => {
    try {
      // Handle Firestore Timestamp
      if (timestamp && typeof timestamp.toDate === 'function') {
        return format(timestamp.toDate(), 'MMM d, yyyy h:mm:ss a');
      }
      // Handle regular Date object or timestamp number
      const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return format(date, 'MMM d, yyyy h:mm:ss a');
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'Invalid Date';
    }
  };

  const sortSessions = (sessions: BookingFunnelStep[]) => {
    return [...sessions].sort((a, b) => {
      // Handle timestamp comparison
      if (orderBy === 'timestamp') {
        const aTimestamp = a.timestamp;
        const bTimestamp = b.timestamp;
        
        // Convert to Date objects if needed
        const aDate = aTimestamp instanceof Date ? aTimestamp : 
                     (aTimestamp && 'toDate' in aTimestamp) ? (aTimestamp as Timestamp).toDate() : 
                     new Date(aTimestamp);
        
        const bDate = bTimestamp instanceof Date ? bTimestamp : 
                     (bTimestamp && 'toDate' in bTimestamp) ? (bTimestamp as Timestamp).toDate() : 
                     new Date(bTimestamp);
        
        // Compare timestamps
        return order === 'asc' ? aDate.getTime() - bDate.getTime() : bDate.getTime() - aDate.getTime();
      }
      
      // Handle string comparison for other fields
      const aValue = String(a[orderBy] || '');
      const bValue = String(b[orderBy] || '');
      
      return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Booking Funnel Analysis
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total Sessions
              </Typography>
              <Typography variant="h3">
                {uniqueSessions.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Completion Rate
              </Typography>
              <Typography variant="h3">
                {completionRate.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Abandonment Rate
              </Typography>
              <Typography variant="h3">
                {abandonmentRate.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Status</InputLabel>
          <Select
              value={status}
            label="Filter by Status"
            onChange={(e) => setStatus(e.target.value as BookingFunnelStep['status'])}
            >
              <MenuItem value="">All</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="abandoned">Abandoned</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
              <TableCell>Session ID</TableCell>
              <TableCell>Current Step</TableCell>
                <TableCell>Status</TableCell>
              <TableCell>Customer ID</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'timestamp'}
                  direction={orderBy === 'timestamp' ? order : 'asc'}
                  onClick={() => handleRequestSort('timestamp')}
                >
                  Last Updated
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
            {sortSessions(sessions).map((session) => (
              <TableRow key={session.id}>
                <TableCell>{session.sessionId}</TableCell>
                <TableCell>{session.step.replace(/_/g, ' ').toUpperCase()}</TableCell>
                  <TableCell>
                    <Chip
                    label={session.status}
                    color={
                      session.status === 'completed'
                        ? 'success'
                        : session.status === 'abandoned'
                        ? 'error'
                        : 'warning'
                    }
                      size="small"
                    />
                  </TableCell>
                <TableCell>{session.customerId}</TableCell>
                <TableCell>
                  {formatTimestamp(session.timestamp)}
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => setSelectedSession(session)}
                    title="View Details"
                  >
                    <Visibility />
                  </IconButton>
                </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

      {selectedSession && (
        <BookingSessionDetail
          session={selectedSession}
          open={!!selectedSession}
          onClose={() => setSelectedSession(null)}
        />
      )}
    </Box>
  );
};

export default BookingFunnelDashboard; 